import { Alert, Button } from '@mui/material';
import NextLink from 'next/link';
import { useSession } from 'components/SessionContext';
import { connectionDisplayName, INTEGRATIONS_SETTINGS_PATH } from 'components/integrations/providerRegistry';
import { type Connection, useConnections } from 'hooks/useConnections';

const SENDER_TYPES = ['gmail-sender', 'microsoft-sender'];

/** Sender accounts can be shared, but only the user who created one is warned to reconnect it. */
const isWarnedUser = (connection: Connection, userId?: string) =>
  !SENDER_TYPES.includes(connection.type) || connection.ownerUserId === userId;

/**
 * One app-bar warning for broken OAuth connections. Hidden while a billing alert is already showing.
 */
const ConnectionAlert = () => {
  const { user, isTrialExpired } = useSession();
  const billingWarningVisible = Boolean(isTrialExpired || user?.isReadOnly);
  const { connections } = useConnections({ enabled: Boolean(user) && !billingWarningVisible });

  if (billingWarningVisible) {
    return null;
  }

  const broken = connections
    .filter(connection => connection.status === 'reconnect_required' && isWarnedUser(connection, user?._id))
    .sort((a, b) => {
      const aTime = a.occurredAt ? new Date(a.occurredAt).getTime() : 0;
      const bTime = b.occurredAt ? new Date(b.occurredAt).getTime() : 0;
      return bTime - aTime;
    });

  const latest = broken[0];

  if (!latest) {
    return null;
  }

  const name = connectionDisplayName(latest.type, latest.label);
  const message =
    broken.length === 1
      ? latest.email
        ? `${name} (${latest.email}) needs to be reconnected.`
        : `${name} needs to be reconnected.`
      : `${broken.length} integrations need to be reconnected.`;

  return (
    <Alert
      severity="error"
      action={
        <Button
          component={NextLink}
          href={INTEGRATIONS_SETTINGS_PATH}
          color="warning"
          variant="contained"
          size="small"
          sx={{ whiteSpace: 'nowrap' }}
        >
          {broken.length === 1 ? 'Reconnect' : 'Review integrations'}
        </Button>
      }
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        flexShrink: 0,
        py: 0.5,
        fontSize: '0.75rem',
        whiteSpace: 'nowrap',
        '& .MuiAlert-icon': { py: 0, marginTop: 0 },
        '& .MuiAlert-message': { py: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
        '& .MuiAlert-action': { py: 0, alignItems: 'center' },
      }}
    >
      {message}
    </Alert>
  );
};

export default ConnectionAlert;
